import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearch } from '@tanstack/react-router';
import { createFileRoute } from '@tanstack/react-router';
import { useGetDevicesByIdQuery, useGetDevicesByIdStatsQuery } from '@/services';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertTriangle, Settings2, BarChart3, Gauge, Eye } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

import { DeviceHeader } from '@/components/devices/device-header';
import { DeviceStatusBar } from '@/components/devices/device-status-bar';
import { OverviewTab } from '@/components/devices/tabs/overview-tab';
import { SensorsTab } from '@/components/devices/tabs/sensors-tab';
import { AnalyticsTab } from '@/components/devices/tabs/analytics-tab';
import { AlertsTab } from '@/components/devices/tabs/alerts-tab';
import { SettingsTab } from '@/components/devices/tabs/settings-tab';
import { DeviceModal } from '@/components/devices/device-modal';
import { DeleteDeviceModal } from '@/components/devices/delete-device-modal';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { z } from 'zod';

export const Route = createFileRoute('/_authenticated/(user-dashboard)/dashboard/_dashboard/devices/$deviceId')({
  component: DeviceDetail,
  validateSearch: z.object({
    tab: z.enum(['overview', 'sensors', 'analytics', 'alerts', 'settings']).optional(),
  }),
});

const timeRangeOptions = [
  { value: '1', label: '1 hour' },
  { value: '6', label: '6 hours' },
  { value: '24', label: 'Last 24 hours' },
  { value: '72', label: '3 days' },
  { value: '168', label: '7 days' },
  { value: '720', label: '30 days' },
  { value: '1440', label: '60 days' },
  { value: '4320', label: '180 days' },
];

// Device is considered online if any sensor has data within this time threshold
const ONLINE_THRESHOLD_MINUTES = 6;

function DeviceDetail() {
  const { deviceId } = useParams({
    from: '/_authenticated/(user-dashboard)/dashboard/_dashboard/devices/$deviceId',
  });
  const { tab: tabFromSearch } = useSearch({
    from: '/_authenticated/(user-dashboard)/dashboard/_dashboard/devices/$deviceId',
  });
  const navigate = useNavigate({
    from: Route.fullPath,
  });
  const [refreshInterval, setRefreshInterval] = useState<number | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [timeRange, setTimeRange] = useState('24');
  const [aggregation, setAggregation] = useState('latest');
  const [selectedTab, setSelectedTab] = useState(tabFromSearch || 'overview');

  // Set default time range based on the selected tab
  React.useEffect(() => {
    if (tabFromSearch) {
      setSelectedTab(tabFromSearch);
    }
  }, [tabFromSearch]);

  // Main device data query
  const {
    data: deviceData,
    isLoading: isLoadingDevice,
    refetch,
  } = useGetDevicesByIdQuery({
    id: deviceId,
    includeSensors: 'true',
    includeLatestData: 'true',
  });

  // Stats data query - pass this to both overview and sensors tabs
  const {
    data: statsData,
    isLoading: isLoadingStats,
    isFetching: isFetchingStats,
    refetch: refetchStats,
  } = useGetDevicesByIdStatsQuery(
    {
      id: deviceId,
      hours: timeRange,
      aggregation: aggregation as 'latest' | 'average' | 'min' | 'max',
    },
    {
      // This will refetch when params change
      refetchOnMountOrArgChange: true,
      // Don't poll automatically to avoid loading state issues
      pollingInterval: 0,
    },
  );

  // Memoize the sensor stats to avoid unnecessary re-renders
  const sensorStats = useMemo(() => {
    return statsData?.data?.sensor_stats || [];
  }, [statsData]);

  // Determine online status based on recent sensor data
  const isOnline = useMemo(() => {
    if (!deviceData?.data) return false;

    const device = deviceData.data;
    const sensors = device.sensors || [];

    // If no sensors, consider offline
    if (sensors.length === 0) return false;

    // Check if any sensor has recent data (within the threshold)
    const thresholdTime = new Date(Date.now() - ONLINE_THRESHOLD_MINUTES * 60 * 1000);

    return sensors.some((sensor) => {
      if (!sensor.latest_data?.timestamp) return false;
      const lastUpdate = new Date(sensor.latest_data.timestamp);
      return lastUpdate > thresholdTime;
    });
  }, [deviceData]);

  // Get alert count from device data
  const alertCount = useMemo(() => {
    if (!deviceData) return 0;
    const count = deviceData.data.alert_count || 0;
    return count > 0 ? count : 0;
  }, [deviceData]);

  // Set up automatic refresh
  useEffect(() => {
    if (refreshInterval) {
      const interval = setInterval(() => {
        refetch();
      }, refreshInterval);

      return () => clearInterval(interval);
    }
  }, [refreshInterval, refetch]);

  const toggleRefresh = () => {
    if (refreshInterval) {
      setRefreshInterval(null);
    } else {
      setRefreshInterval(5000);
      refetch();
    }
  };

  if (isLoadingDevice) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex flex-col lg:flex-row justify-between gap-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-md" />
            <div>
              <Skeleton className="h-8 w-48 mb-2" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-36" />
            <Skeleton className="h-9 w-24" />
            <Skeleton className="h-9 w-16" />
            <Skeleton className="h-9 w-20" />
          </div>
        </div>

        {/* Status Bar Skeleton */}
        <Card className="border-none shadow-none bg-muted/50">
          <CardContent className="p-4 flex justify-between items-center">
            <div className="flex gap-4">
              <div className="flex items-center">
                <Skeleton className="w-3 h-3 rounded-full mr-2" />
                <Skeleton className="h-4 w-16" />
              </div>
              <div className="flex items-center">
                <Skeleton className="h-4 w-20 mr-2" />
                <Skeleton className="h-4 w-24" />
              </div>
              <div className="flex items-center">
                <Skeleton className="h-4 w-24 mr-2" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
            <Skeleton className="h-6 w-20" />
          </CardContent>
        </Card>

        {/* Tabs Skeleton */}
        <div className="space-y-4">
          <div className="flex space-x-1 bg-muted p-1 rounded-lg w-fit">
            <Skeleton className="h-8 w-20 rounded-md" />
            <Skeleton className="h-8 w-20 rounded-md" />
            <Skeleton className="h-8 w-20 rounded-md" />
            <Skeleton className="h-8 w-16 rounded-md" />
            <Skeleton className="h-8 w-20 rounded-md" />
          </div>

          {/* Content Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="h-64 w-full rounded-lg" />
            <Skeleton className="h-64 w-full rounded-lg" />
            <Skeleton className="h-64 w-full rounded-lg" />
          </div>
          <Skeleton className="h-96 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  if (!deviceData?.data) {
    return <DeviceNotFound />;
  }

  const device = deviceData.data;
  const sensors = device.sensors || [];

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <DeviceHeader
        device={device}
        isOnline={isOnline}
        refreshInterval={refreshInterval}
        toggleRefresh={toggleRefresh}
        openEditModal={() => setIsEditModalOpen(true)}
        openDeleteModal={() => setIsDeleteModalOpen(true)}
      />

      {/* Status Bar */}
      <DeviceStatusBar device={device} isOnline={isOnline} alertCount={alertCount} sensors={sensors} />

      {/* Enhanced Tabs */}
      <Tabs
        defaultValue="overview"
        value={selectedTab}
        onValueChange={(value) => {
          navigate({
            search: {
              tab: value as typeof selectedTab,
            },
          });
          setSelectedTab(value as typeof selectedTab);
        }}
        className="space-y-6"
      >
        <div className="border-b border-border">
          <TabsList className="h-12 p-1 bg-transparent">
            <TabsTrigger
              value="overview"
              className="h-10 px-4 gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm"
            >
              <Eye className="h-4 w-4" />
              Overview
              {alertCount > 0 && (
                <Badge variant="destructive" className="ml-1 px-1.5 py-0.5 text-xs">
                  {alertCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="sensors"
              className="h-10 px-4 gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm"
            >
              <Gauge className="h-4 w-4" />
              Sensors
              {sensors.length > 0 && (
                <Badge variant="secondary" className="ml-1 px-1.5 py-0.5 text-xs">
                  {sensors.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="analytics"
              className="h-10 px-4 gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm"
            >
              <BarChart3 className="h-4 w-4" />
              Analytics
            </TabsTrigger>
            <TabsTrigger
              value="alerts"
              className="h-10 px-4 gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm"
            >
              <AlertTriangle className="h-4 w-4" />
              Alerts
              {alertCount > 0 && (
                <Badge variant="destructive" className="ml-1 px-1.5 py-0.5 text-xs">
                  {alertCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="settings"
              className="h-10 px-4 gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm"
            >
              <Settings2 className="h-4 w-4" />
              Settings
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab
            device={device}
            sensors={sensors}
            sensorStats={sensorStats}
            timeRange={timeRange}
            setTimeRange={setTimeRange}
            timeRangeOptions={timeRangeOptions}
            alertCount={alertCount}
            setSelectedTab={(tab) => setSelectedTab(tab as typeof selectedTab)}
          />
        </TabsContent>

        <TabsContent value="sensors" className="mt-6">
          <SensorsTab
            deviceId={deviceId}
            sensors={sensors}
            sensorStats={sensorStats}
            isLoading={isLoadingStats}
            isFetching={isFetchingStats}
            timeRange={timeRange}
            setTimeRange={setTimeRange}
            aggregation={aggregation}
            setAggregation={setAggregation}
            timeRangeOptions={timeRangeOptions}
            refetch={() => {
              refetch();
              refetchStats();
            }}
          />
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          <AnalyticsTab
            deviceId={deviceId}
            timeRange={timeRange}
            setTimeRange={setTimeRange}
            timeRangeOptions={timeRangeOptions}
          />
        </TabsContent>

        <TabsContent value="alerts" className="mt-6">
          <AlertsTab device={device} />
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <SettingsTab device={device} onEditClick={() => setIsEditModalOpen(true)} />
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <DeviceModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        mode="edit"
        device={device}
        onSuccess={() => {
          setIsEditModalOpen(false);
          refetch();
        }}
      />

      <DeleteDeviceModal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} deviceId={deviceId} />
    </div>
  );
}

function DeviceNotFound() {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center justify-center h-full">
      <h2 className="text-xl font-bold">Device not found</h2>
      <p className="text-muted-foreground mt-2">The requested device could not be found.</p>
      <Button variant="outline" className="mt-6" onClick={() => navigate({ to: '/dashboard/devices' })}>
        Return to Devices List
      </Button>
    </div>
  );
}
